/* eslint-disable no-unused-vars */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  Typography
} from '@mui/material';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

export default function SingleLocationMapAccordion({
  lat,
  lng,
  label = 'Location'
}) {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);

  // Postgres DOUBLE/DECIMAL columns come back as strings over JSON, and
  // Leaflet's LatLng rejects those as firmly as it rejects undefined — so
  // coerce, then validate.
  const numericLat = Number(lat);
  const numericLng = Number(lng);
  const hasCoords =
    lat !== null &&
    lat !== undefined &&
    lng !== null &&
    lng !== undefined &&
    Number.isFinite(numericLat) &&
    Number.isFinite(numericLng);

  useEffect(() => {
    if (!hasCoords) {
      // Skip the pointless reverse-geocode request for lat=&lon=.
      setLoading(false);
      return;
    }

    async function fetchAddress() {
      try {
        const res = await axios.get(
          'https://nominatim.openstreetmap.org/reverse',
          {
            params: {
              format: 'jsonv2',
              lat,
              lon: lng
            }
          }
        );
        setAddress(res.data.display_name || 'Unknown address');
      } catch (err) {
        setAddress('Failed to fetch address');
      } finally {
        setLoading(false);
      }
    }
    fetchAddress();
  }, [lat, lng, hasCoords]);

  // Early return AFTER the hooks above, so hook order stays stable.
  // Previously this component rendered <MapContainer center={[undefined,
  // undefined]}> whenever a package had no scan coordinates; Leaflet throws
  // "Invalid LatLng object", and since the nearest ErrorBoundary wraps the
  // whole router the entire Package Details page went blank — every tab, not
  // just the map.
  if (!hasCoords) {
    return (
      <Grid item xs={12} sm={6}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              No coordinates recorded for this package.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={6}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            <MapContainer
              center={[numericLat, numericLng]}
              zoom={13}
              scrollWheelZoom={false}
              style={{ height: 250, width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <Marker position={[numericLat, numericLng]}>
                <Popup>{address || 'Fetching address...'}</Popup>
              </Marker>
            </MapContainer>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Address:
          </Typography>
          <Typography variant="body2">
            {loading ? <CircularProgress size={16} /> : address}
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
